import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/supabase-auth';
import { logger } from '@/lib/logger';

// Aligned with database app_role enum
// Note: 'user' is legacy, equivalent to 'parent'
type AppRole = 'admin' | 'organization_admin' | 'parent' | 'guest' | 'user';

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  organization_id?: string;
}

export function useUserRole() {
  const { session } = useAuth();

  // Only treat roles as "loading" when we actually intend to fetch them.
  // React Query v5 can report isPending=true even for disabled queries.
  const enabled = !!session?.user?.id && !!session?.access_token;

  // PHASE 0: Gate on session.user.id AND access_token to prevent 401s
  const { data: roles, isPending, refetch } = useQuery({
    queryKey: ['user-roles', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id || !session?.access_token) return [];

      const { data, error } = await supabase.rpc('get_user_roles_secure', { 
        p_user_id: session.user.id 
      });

      if (error) {
        logger.warn('[useUserRole] get_user_roles_secure failed:', error);

        // Retry-once on 401 (transient auth issue)
        if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
          console.info('[useUserRole] Retrying after 401...');
          await new Promise((resolve) => setTimeout(resolve, 500));

          const { data: retryData, error: retryError } = await supabase.rpc(
            'get_user_roles_secure', { p_user_id: session.user.id }
          );

          if (retryError) {
            logger.error('[useUserRole] Retry failed:', retryError);
            return [];
          }

          const retryArr = Array.isArray(retryData) ? retryData : [];
          return retryArr.map((r: { role: string; organization_id: string }) => ({
            id: r.role as AppRole,
            user_id: session.user.id,
            role: r.role as AppRole,
            organization_id: r.organization_id,
          }));
        }

        return [];
      }

      // Type-safe RPC response mapping
      const dataArr = Array.isArray(data) ? data : [];
      return dataArr.map((r: { role: string; organization_id: string }) => ({
        id: r.role as AppRole,
        user_id: session.user.id,
        role: r.role as AppRole,
        organization_id: r.organization_id,
      }));
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const hasRole = (role: AppRole, organizationId?: string) => {
    if (!roles || roles.length === 0) return false;

    const isLegacyParentRole = (currentRole: AppRole) => {
      if (role === 'parent' || role === 'user') {
        return currentRole === 'parent' || currentRole === 'user';
      }
      return currentRole === role;
    };

    if (organizationId) {
      return roles.some(r => isLegacyParentRole(r.role) && r.organization_id === organizationId);
    }

    return roles.some(r => isLegacyParentRole(r.role));
  };

  const isAdmin = () => {
    return hasRole('admin');
  };

  const isOrgAdmin = (organizationId?: string) => {
    // SECURITY: ONLY organization_admin role has org admin privileges
    // Platform admins (admin) are explicitly excluded from org operations
    if (organizationId) {
      return hasRole('organization_admin', organizationId);
    }
    return hasRole('organization_admin');
  };

  const primaryRole = roles && roles.length > 0
    ? roles.sort((a, b) => {
        const roleOrder: Record<AppRole, number> = {
          admin: 1,
          organization_admin: 2,
          parent: 3,
          user: 3, // Legacy role, same priority as parent
          guest: 4,
        };
        return roleOrder[a.role] - roleOrder[b.role];
      })[0]
    : null;

  return {
    roles: roles || [],
    primaryRole,
    hasRole,
    isAdmin,
    isOrgAdmin,
    isLoading: enabled ? isPending : false,
    refetch, // Expose refetch for manual refresh after role changes
  };
}
